import * as fileApi from '@/api/file';
import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { App, Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import pathbe from 'path-browserify-esm';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Editor, Structure } from '@aitomol/core';
import { structureSummary } from '@aitomol/core';
import { chargeMultiplicityOf, gaussianInfoOf } from './types';

/**
 * Document summary plus export/save actions.
 *
 * Everything here is new relative to the JSmol iframe, which could only display
 * what the server handed it: the structure can now be written back into the lab
 * file system, and exported as PNG / GLB / animated GIF / any supported format.
 */
export interface DocumentPanelProps {
    editor: Editor | null;
    structure: Structure | null;
    path?: string;
    /** Called after the structure was written back, so the file tree can refresh. */
    onSaved?: () => void;
}

interface DocumentPanelBodyProps {
    editor: Editor;
    structure: Structure;
    path?: string;
    onSaved?: () => void;
}

function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function bytesToBlob(data: Uint8Array, mimeType: string): Blob {
    return new Blob([data.slice().buffer as ArrayBuffer], { type: mimeType });
}

/**
 * Split from the exported component so the callbacks can assume a live editor:
 * `exportData`/`snapshot` are only reachable once both props exist, and a
 * null-guard placed after the hooks would not narrow anything for TypeScript.
 */
const DocumentPanelBody: React.FC<DocumentPanelBodyProps> = ({ editor, structure, path, onSaved }) => {
    const { message, modal } = App.useApp();
    const { t } = useTranslation('common');

    const summary = useMemo(() => structureSummary(structure), [structure]);
    const gaussian = gaussianInfoOf(structure);
    const chargeMultiplicity = chargeMultiplicityOf(structure);
    const baseName = path ? pathbe.basename(path, pathbe.extname(path)) : 'structure';

    /** Formats that can actually be written, so the menu never offers a dead end. */
    const writable = useMemo(
        () =>
            editor.formats
                .list()
                .filter((plugin) => typeof plugin.serialize === 'function')
                .map((plugin) => ({ id: plugin.id, label: plugin.label ?? plugin.id, extensions: plugin.extensions })),
        [editor]
    );

    const sourceExtension = path ? pathbe.extname(path).replace('.', '').toLowerCase() : '';
    const savePlugin = writable.find((plugin) => plugin.extensions.includes(sourceExtension));

    const saveToLab = (): void => {
        if (!path || !savePlugin) return;
        modal.confirm({
            title: t('aitomol.save_confirm_title'),
            content: t('aitomol.save_confirm_body', { path: pathbe.basename(path) }),
            okText: t('aitomol.save'),
            cancelText: t('aitomol.cancel'),
            onOk: async () => {
                try {
                    const data = await editor.exportData(savePlugin.id);
                    if (typeof data !== 'string') {
                        message.error(t('aitomol.save_binary_unsupported'));
                        return;
                    }
                    const response = await fileApi.saveFileResource(path, data);
                    if (!response?.ok) {
                        message.error(response?.message ?? t('aitomol.save_failed'));
                        return;
                    }
                    message.success(t('aitomol.save_ok'));
                    onSaved?.();
                } catch (error) {
                    console.error('[aitomol] save failed', error);
                    message.error(t('aitomol.save_failed'));
                }
            }
        });
    };

    const exportStructure = async (formatId: string): Promise<void> => {
        try {
            const plugin = editor.formats.get(formatId);
            const extension = plugin?.exportExtension ?? plugin?.extensions[0] ?? 'dat';
            await editor.download(formatId, `${baseName}.${extension}`);
        } catch (error) {
            console.error('[aitomol] export failed', error);
            message.error(t('aitomol.export_failed'));
        }
    };

    const exportSnapshot = async (): Promise<void> => {
        const blob = await editor.snapshot({ format: 'png', background: 'transparent' });
        if (!blob) {
            message.error(t('aitomol.export_failed'));
            return;
        }
        downloadBlob(blob, `${baseName}.png`);
    };

    const exportGlb = async (): Promise<void> => {
        const blob = await editor.backend?.exportGLB?.();
        if (!blob) {
            message.warning(t('aitomol.glb_unsupported'));
            return;
        }
        downloadBlob(blob, `${baseName}.glb`);
    };

    const exportGif = async (): Promise<void> => {
        const data = await editor.exportGIF({ delayMs: 100, loop: 0 });
        if (!data) {
            message.warning(t('aitomol.gif_unsupported'));
            return;
        }
        downloadBlob(bytesToBlob(data, 'image/gif'), `${baseName}.gif`);
    };

    const exportItems: MenuProps['items'] = [
        ...writable.map((plugin) => ({ key: plugin.id, label: plugin.label })),
        { type: 'divider' as const },
        { key: '__png', label: t('aitomol.export_png') },
        { key: '__glb', label: t('aitomol.export_glb') },
        { key: '__gif', label: t('aitomol.export_gif') }
    ];

    const onExportClick: MenuProps['onClick'] = ({ key }) => {
        if (key === '__png') void exportSnapshot();
        else if (key === '__glb') void exportGlb();
        else if (key === '__gif') void exportGif();
        else void exportStructure(key);
    };

    return (
        <div className="aitomol-panel aitomol-panel-wide">
            <div className="aitomol-panel-title">
                <span>{path ? pathbe.basename(path) : t('aitomol.document')}</span>
                {gaussian && !gaussian.completed ? <span className="aitomol-warn">{t('aitomol.run_incomplete')}</span> : null}
            </div>

            <div className="aitomol-panel-foot">
                <span className="aitomol-hint">{summary?.formula || '—'}</span>
                <span className="aitomol-hint">
                    {summary?.atoms ?? 0} {t('aitomol.atoms')} · {summary?.bonds ?? 0} {t('aitomol.bonds')}
                </span>
            </div>

            {chargeMultiplicity.charge !== null || chargeMultiplicity.multiplicity !== null ? (
                <div className="aitomol-panel-foot">
                    <span className="aitomol-hint">
                        {t('aitomol.charge')} {chargeMultiplicity.charge ?? '—'} · {t('aitomol.multiplicity')}{' '}
                        {chargeMultiplicity.multiplicity ?? '—'}
                    </span>
                </div>
            ) : null}

            {gaussian ? (
                <>
                    {gaussian.method || gaussian.basisSet ? (
                        <div className="aitomol-panel-foot">
                            <span className="aitomol-hint">
                                {gaussian.method}
                                {gaussian.basisSet ? ` / ${gaussian.basisSet}` : ''}
                            </span>
                        </div>
                    ) : null}
                    {gaussian.energies.length > 0 ? (
                        <div className="aitomol-panel-foot">
                            <span className="aitomol-hint">E = {gaussian.energies[gaussian.energies.length - 1].toFixed(6)} Ha</span>
                            {gaussian.frameCount > 1 ? (
                                <span className="aitomol-hint">
                                    {gaussian.frameCount} {t('aitomol.steps')}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                    {gaussian.route ? (
                        <div className="aitomol-route" title={gaussian.route}>
                            {gaussian.route}
                        </div>
                    ) : null}
                </>
            ) : null}

            <div className="aitomol-actions">
                <Button size="small" icon={<SaveOutlined />} disabled={!savePlugin || !path} onClick={saveToLab}>
                    {t('aitomol.save')}
                </Button>
                <Dropdown menu={{ items: exportItems, onClick: onExportClick }} trigger={['click']} placement="bottomLeft">
                    <Button size="small" icon={<DownloadOutlined />}>
                        {t('aitomol.export')}
                    </Button>
                </Dropdown>
            </div>
        </div>
    );
};

export const DocumentPanel: React.FC<DocumentPanelProps> = ({ editor, structure, path, onSaved }) => {
    if (!editor || !structure) return null;
    return <DocumentPanelBody editor={editor} structure={structure} path={path} onSaved={onSaved} />;
};